import type { CSSProperties } from 'react'
import { displayAmount, getSmartDecimals } from 'src/utils/displayAmount'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import { useCountUp } from './useCountUp'

type Props = Readonly<{
  /** Sum of `potentialPayout` across every won bet in the round. */
  amount: number
  /** Currency symbol (e.g. "$", "€") prepended to the amount. */
  currencySymbol: string
  /**
   * Delay so the banner lands AFTER the per-option pills have popped
   * and counted up — turns a parallel celebration into a sequenced one
   * (per-option pills pop → numbers roll → "TOTAL WIN" banner caps it).
   */
  delayMs: number
}>

/**
 * The capstone callout for a settled round in which the player won at
 * least one bet. Shows "TOTAL WIN €18.40" in dark-glass-with-gold-trim,
 * matching the per-option pill design language but bigger and with
 * its own count-up. Lands last so it reads as the summary moment.
 */
export function TotalWinBanner({ amount, currencySymbol, delayMs }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  const decimals = getSmartDecimals(amount)
  // Count-up runs slightly longer than per-option pills (900ms) so the
  // total feels like the "big" number even when it's only marginally
  // larger than a single bet's payout.
  const animated = useCountUp(amount, 900, delayMs + 200)

  return (
    <div
      className="chip-panel__total-win"
      role="status"
      aria-live="polite"
      style={{ '--win-pill-delay': `${delayMs}ms` } as CSSProperties}
    >
      <span className="chip-panel__total-win-label">{t('Total Win')}</span>
      <span className="chip-panel__total-win-amount">
        {currencySymbol}
        {displayAmount(animated, true, decimals)}
      </span>
    </div>
  )
}
